import { EVENT_TYPE } from "@/api/analytics/api"
import { useCreateGameEvent } from "@/api/analytics/hooks"
import { useGetBonusQuestion, useUpdatePlayerBonusQuestion } from "@/api/bonusQuestion/hooks"
import useGetComicsDetail from "@/api/hooks/useComicsDetail"
import BonusQuestion from "@/components/pages/CartoonDetailPage/BonusQuestionSection/BonusQuestion/BonusQuestion"
import BonusQuestionSectionContext from "@/components/pages/CartoonDetailPage/BonusQuestionSection/BonusQuestionSectionContext"
import GameResultPage from "@/components/pages/CartoonDetailPage/GameResultPage"
import Container from "@/components/shared/Container"
import RoundedButton from "@/components/shared/RoundedButton"
import { ROUTES } from "@/conf"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useContext, useEffect } from "react"

interface BonusQuestionSectionProps {
  bonusQuestionId: string
  comicsBackgroundImageUrl: string
  comicsName: string
  comicsSlug: string
}

const BonusQuestionSection = ({
  bonusQuestionId,
  comicsBackgroundImageUrl,
  comicsName,
  comicsSlug,
}: BonusQuestionSectionProps) => {
  const router = useRouter()
  const params = useParams()

  const { coins, answerState } = useContext(BonusQuestionSectionContext)

  const { bonusQuestion, isLoading } = useGetBonusQuestion(bonusQuestionId)

  const { mutateAsync: createAnalytics } = useCreateGameEvent()

  const {
    mutateAsync: updatePlayerCoinsForBonusQuestion,
    isSuccess,
    isLoading: isLoadingUpdatePlayerCoinsForBonusQuestion,
  } = useUpdatePlayerBonusQuestion()

  const { comicsDetail } = useGetComicsDetail(comicsSlug)

  useEffect(() => {
    if (!isLoadingUpdatePlayerCoinsForBonusQuestion && isSuccess) {
      router.push(`${ROUTES.cartoonDetail}/${params.slug}?section=bonus&bonusConfirmation=true`)
    }
  }, [isLoadingUpdatePlayerCoinsForBonusQuestion, isSuccess, params.slug, router])

  useEffect(() => {
    if (coins > 0 && !bonusQuestion?.claimed && answerState === "success") {
      updatePlayerCoinsForBonusQuestion({ body: { gainedGroshak: coins }, bonusQuestionId: bonusQuestionId })
      createAnalytics({
        comicId: comicsDetail?.comicId || "",
        time: Date.now(),
        event: EVENT_TYPE.BONUS_CLAIMED,
      })
    }
  }, [
    answerState,
    bonusQuestion?.claimed,
    bonusQuestionId,
    coins,
    comicsDetail?.comicId,
    createAnalytics,
    updatePlayerCoinsForBonusQuestion,
  ])

  useEffect(() => {
    if (!bonusQuestion && !isLoading) {
      router.push(`${ROUTES.cartoonDetail}/${params.slug}`)
    }
  }, [bonusQuestion, isLoading, params.slug, router])

  if (!isLoadingUpdatePlayerCoinsForBonusQuestion && isSuccess) {
    return (
      <GameResultPage
        gainedGroshaks={coins}
        comicsBackgroundImageUrl={comicsBackgroundImageUrl}
        comicsName={comicsName}
      />
    )
  }

  return (
    bonusQuestion &&
    !isLoading && (
      <div className="bg-stateBonus-100 bg-cover pb-16 pt-8	">
        <Container className="relative flex h-[calc(100vh-80px)] justify-center">
          <RoundedButton
            onClick={() => router.push(`${ROUTES.cartoonDetail}/${params.slug}`)}
            className="absolute left-6 top-0 z-20"
            icon={<Image src="/assets/images/arrow_left.svg" width={32} height={32} alt="" />}
          />
          <div className="relative h-full w-full max-w-[500px]">
            <BonusQuestion data={bonusQuestion} />
          </div>
        </Container>
      </div>
    )
  )
}

export default BonusQuestionSection
